<script setup lang="ts">
  import { ref, computed, watch, shallowRef, markRaw, type Component } from 'vue';
  import {
    ComboboxRoot,
    ComboboxInput,
    ComboboxItem,
    ComboboxEmpty,
    ComboboxViewport,
  } from 'reka-ui';
  import { Check, ChevronsUpDown, Search, SearchX, X } from 'lucide-vue-next';
  import { Popover, PopoverTrigger } from '@/shared/ui/popover';
  // Import PopoverContent directly from reka-ui (not the local shadcn wrapper) to skip
  // PopoverPortal, which conflicts with vue-final-modal's focus trap in modal contexts.
  import { PopoverContent } from 'reka-ui';
  import { cn } from '@/shared/lib/utils';
  import IconImage from '../common/IconImage.vue';
  import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';

  export interface SearchComboboxOption {
    value: string;
    label: string;
    icon?: Component;
  }

  interface CachedOption {
    label: string;
    icon?: Component;
  }

  interface Props {
    modelValue: string;
    options: SearchComboboxOption[];
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    loading?: boolean;
    initialLabel?: string;
    class?: string;
    triggerIcon?: 'chevrons' | 'search' | 'arrow-down';
    ariaLabelledby?: string;
    creatable?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    placeholder: 'Selecione...',
    searchPlaceholder: 'Buscar...',
    emptyMessage: 'Nenhum resultado encontrado.',
    disabled: false,
    loading: false,
    initialLabel: undefined,
    class: undefined,
    triggerIcon: 'chevrons',
    creatable: false,
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
    (e: 'search', value: string): void;
    (e: 'create', value: string): void;
  }>();

  const open = ref(false);
  const searchTerm = ref('');
  const hasNavigatedOptions = ref(false);
  const inputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);
  // shallowRef so the cached icon components are never made deeply reactive.
  const optionCache = shallowRef<Record<string, CachedOption>>({});

  function cacheLabel(value: string, label: string) {
    optionCache.value = {
      ...optionCache.value,
      [value]: { ...optionCache.value[value], label },
    };
  }

  if (props.initialLabel && props.modelValue) {
    cacheLabel(props.modelValue, props.initialLabel);
  }

  watch(
    () => props.initialLabel,
    (newLabel) => {
      if (newLabel && props.modelValue) {
        cacheLabel(props.modelValue, newLabel);
      }
    }
  );

  watch(
    () => props.options,
    (newOptions) => {
      const next = { ...optionCache.value };
      for (const option of newOptions) {
        next[option.value] = {
          label: option.label,
          icon: option.icon ? markRaw(option.icon) : undefined,
        };
      }
      optionCache.value = next;
    },
    { immediate: true }
  );

  const selectedLabel = computed(() => {
    if (!props.modelValue) return '';
    return optionCache.value[props.modelValue]?.label ?? props.initialLabel ?? '';
  });

  const selectedIcon = computed<Component | undefined>(() => {
    if (!props.modelValue) return undefined;
    return optionCache.value[props.modelValue]?.icon;
  });

  function handleSelect(value: string | Record<string, string>) {
    const selected = typeof value === 'string' ? value : '';
    emit('update:modelValue', selected);
    open.value = false;
  }

  function handleClear() {
    emit('update:modelValue', '');
  }

  watch(
    open,
    (newValue) => {
      if (newValue) {
        inputRef.value?.$el?.focus();
      } else {
        searchTerm.value = '';
      }
    },
    { flush: 'post' }
  );

  watch(searchTerm, (newValue) => {
    hasNavigatedOptions.value = false;
    emit('search', newValue);
  });

  function handleSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      hasNavigatedOptions.value = true;
      return;
    }
    if (event.key !== 'Enter' || !props.creatable || hasNavigatedOptions.value) return;

    const term = searchTerm.value.trim();
    if (!term) return;

    event.preventDefault();
    event.stopPropagation();
    emit('create', term);
    open.value = false;
  }
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <button
        type="button"
        role="combobox"
        :aria-expanded="open"
        :aria-labelledby="ariaLabelledby"
        aria-haspopup="listbox"
        :disabled="disabled"
        :data-placeholder="!selectedLabel || undefined"
        :class="
          cn(
            'border-gray-02 text-gray-07 data-[placeholder]:text-gray-03 flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs h-9 disabled:cursor-not-allowed disabled:opacity-50',
            props.class
          )
        "
      >
        <span class="flex min-w-0 flex-1 items-center gap-2">
          <component
            :is="selectedIcon"
            v-if="selectedIcon"
            class="size-6 shrink-0"
            aria-hidden="true"
            data-testid="search-combobox-selected-icon"
          />
          <span class="truncate">{{ selectedLabel || placeholder }}</span>
        </span>
        <X
          v-if="modelValue && !disabled"
          role="button"
          aria-label="Limpar seleção"
          tabindex="0"
          class="size-4 shrink-0 text-gray-03"
          @click.stop="handleClear"
          @keydown.enter.stop="handleClear"
        />
        <Search
          v-else-if="triggerIcon === 'search'"
          class="size-4 shrink-0 text-gray-xx-02"
          aria-hidden="true"
        />
        <IconImage
          v-else-if="triggerIcon === 'arrow-down'"
          :src="ArrowDown"
          alt=""
          class="size-4 shrink-0 text-gray-07"
        />
        <ChevronsUpDown
          v-else
          class="size-4 shrink-0 text-gray-03"
          aria-hidden="true"
        />
      </button>
    </PopoverTrigger>
    <PopoverContent
      :side-offset="4"
      align="center"
      class="bg-white-01 text-gray-07 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[1001] w-[var(--reka-popper-anchor-width)] rounded-lg border border-gray-02 p-0 shadow-lg outline-hidden"
    >
      <ComboboxRoot
        :ignore-filter="true"
        :model-value="modelValue"
        :open="true"
        :reset-search-term-on-select="false"
        :reset-search-term-on-blur="false"
        @update:model-value="handleSelect"
      >
        <div
          class="flex items-center border-b border-gray-02 px-3"
          @keydown.capture="handleSearchKeydown"
        >
          <Search class="mr-2 size-4 shrink-0 text-gray-03" />
          <ComboboxInput
            ref="inputRef"
            v-model="searchTerm"
            :placeholder="searchPlaceholder"
            class="flex h-10 w-full rounded-md bg-transparent py-3 text-sm text-gray-07 outline-none placeholder:text-gray-03 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
        <ComboboxViewport class="max-h-[200px] overflow-y-auto p-1">
          <div
            v-if="loading"
            class="py-6 text-center text-sm text-gray-03"
          >
            Buscando...
          </div>
          <ComboboxEmpty v-else-if="emptyMessage">
            <div class="flex flex-col items-center gap-1.5 px-4 py-5">
              <SearchX class="size-8 text-gray-03/40" />
              <p class="text-sm text-gray-03 text-center leading-snug">
                {{ emptyMessage }}
              </p>
            </div>
          </ComboboxEmpty>
          <ComboboxItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            class="relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[highlighted]:bg-off-white data-[state=checked]:bg-gray-02 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
          >
            <Check
              :class="
                cn(
                  'absolute left-2 size-4',
                  modelValue === option.value ? 'opacity-100' : 'opacity-0'
                )
              "
            />
            <component
              :is="option.icon"
              v-if="option.icon"
              class="size-6 shrink-0"
              aria-hidden="true"
              data-testid="search-combobox-option-icon"
            />
            {{ option.label }}
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxRoot>
    </PopoverContent>
  </Popover>
</template>
